<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text class="orangehrm-main-title" tag="h6">
        {{ $t('payroll.add_payroll_runs') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="payrollRun.name"
                :rules="rules.name"
                :label="$t('payroll.run_name')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="payrollRun.payPeriodId"
                type="select"
                :options="payFrequencies"
                :rules="rules.payPeriodId"
                :label="$t('pim.pay_frequency')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="payrollRun.payDate"
                :label="$t('payroll.pay_date')"
                :rules="rules.payDate"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="payrollRun.employeeSelectionType"
                type="select"
                :options="employeeSelectionOptions"
                :label="$t('payroll.employee_selection_type')"
                :rules="rules.employeeSelectionType"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="payrollRun.periodStartDate"
                :label="$t('payroll.period_start_date')"
                :rules="rules.periodStartDate"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="payrollRun.periodEndDate"
                :label="$t('payroll.period_end_date')"
                :rules="rules.periodEndDate"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="payrollRun.payslipTemplateId"
                type="select"
                :label="$t('payroll.payslips_templates')"
                :options="templateList"
                :rules="rules.templates"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item
              v-if="payrollRun.employeeSelectionType.param === 'manual'"
            >
              <oxd-input-field
                v-model="payrollRun.employeeNumbers"
                value="Select"
                type="multiselect"
                :label="$t('general.employee_name')"
                :options="employeeList"
                :rules="rules.employees"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item
              v-if="payrollRun.employeeSelectionType.param === 'criteria'"
            >
              <oxd-input-field
                v-model="payrollRun.subunitIds"
                value="Select"
                type="multiselect"
                :label="$t('payroll.subunits')"
                :options="subunits"
                :rules="rules.subunits"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item
              v-if="payrollRun.employeeSelectionType.param === 'criteria'"
            >
              <oxd-input-field
                v-model="payrollRun.locationIds"
                value="Select"
                type="multiselect"
                :label="$t('payroll.locations')"
                :options="locations"
                :rules="rules.location"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-label :label="$t('general.configuration')" />
              <div
                v-for="config in configList"
                :key="config"
                style="margin-top: 5px"
              >
                <oxd-chip :class="chipClasses" :label="$t(config)" />
              </div>
              <br />
              <oxd-label
                v-if="isConfigEmpty"
                :label="$t('payroll.no_config')"
                class="no-config"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <div class="orangehrm-button-row">
            <oxd-button
              display-type="ghost"
              :label="$t('general.cancel')"
              @click="onCancel"
            />
            <submit-button />
          </div>
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {navigate} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import {
  endDateShouldBeAfterStartDate,
  required,
  startDateShouldBeBeforeEndDate,
  validDateFormat,
  validSelection,
} from '@/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {OxdChip, OxdLabel} from '@ohrm/oxd';
import {onMounted, ref} from 'vue';
import {formatEmployeeFullName} from '@/core/util/helper/employeeDisplayName';

const payrollRunModel = {
  name: '',
  payPeriodId: null,
  periodStartDate: null,
  periodEndDate: null,
  payDate: '',
  payslipTemplateId: false,
  employeeSelectionType: '',
  employeeNumbers: [],
  locationIds: [],
  subunitIds: [],
  configuration: {},
};

export default {
  components: {'oxd-label': OxdLabel, 'oxd-chip': OxdChip},
  props: {
    subunits: {
      type: Array,
      default: () => [],
    },
    locations: {
      type: Array,
      default: () => [],
    },
    payFrequencies: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    let isConfigEmpty = ref(false);
    let configList = ref({});
    let employeeList = ref([]);
    let templateList = ref([]);
    const {userDateFormat} = useDateFormat();
    const configApi = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/payroll/config`,
    );
    const employeeApi = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees?includeEmployees=currentAndPast`,
    );
    const templatesApi = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/payroll/payslip-templates?isActive=true`,
    );

    onMounted(async () => {
      const configResponse = await configApi.get();
      const {data} = configResponse.data;
      configList.value = Object.keys(data)
        .filter((key) => data[key])
        .map(
          (key) => `payroll.${key.replace(/([A-Z])/g, '_$1').toLowerCase()}`,
        );
      isConfigEmpty.value = Object.values(configList.value).every(
        (value) => value === false,
      );
    });

    onMounted(async () => {
      const templatesResponse = await templatesApi.get();
      const {data} = templatesResponse.data;
      templateList.value = data.map((templates) => {
        return {
          id: templates.id,
          label: templates.templateName,
          _templates: templates,
        };
      });
    });

    onMounted(async () => {
      const employeeResponse = await employeeApi.get();
      const {data} = employeeResponse.data;
      employeeList.value = data.map((emp) => ({
        id: emp.empNumber,
        label: formatEmployeeFullName(emp),
      }));
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/runs',
    );
    return {
      isConfigEmpty,
      configList,
      templateList,
      http,
      userDateFormat,
      employeeList,
    };
  },
  data() {
    return {
      isLoading: false,
      payrollRun: {...payrollRunModel},
      rules: {
        name: [required],
        payPeriodId: [required],
        employees: [required],
        employeeSelectionType: [required, validSelection],
        location: [required],
        subunits: [required],
        templates: [required],
        payDate: [required, validDateFormat(this.userDateFormat)],
        periodStartDate: [
          required,
          validDateFormat(this.userDateFormat),
          startDateShouldBeBeforeEndDate(
            () => this.payrollRun.periodEndDate,
            this.$t('payroll.period_start_date_should_be_before_end_date'),
          ),
        ],
        periodEndDate: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.payrollRun.periodStartDate,
            this.$t('payroll.period_end_date_should_be_after_start_date'),
          ),
        ],
      },
      employeeSelectionOptions: [
        {
          id: 1,
          param: 'manual',
          label: this.$t('payroll.manual'),
        },
        {
          id: 2,
          param: 'criteria',
          label: this.$t('payroll.criteria'),
        },
      ],
    };
  },
  computed: {
    chipClasses() {
      return {
        chip: true,
      };
    },
  },
  mounted() {
    this.$watch(
      () => this.payrollRun.employeeSelectionType,
      (newVal, oldVal) => {
        if (newVal?.param !== oldVal?.param) {
          this.payrollRun.employeeNumbers = [];
          this.payrollRun.subunitIds = [];
          this.payrollRun.locationIds = [];
        }
      },
      {deep: true},
    );
  },
  methods: {
    onCancel() {
      navigate('/payroll/viewPayrollRuns');
    },
    onSave() {
      const subunits = Array.isArray(this.payrollRun.subunitIds)
        ? this.payrollRun.subunitIds
        : [];
      const locations = Array.isArray(this.payrollRun.locationIds)
        ? this.payrollRun.locationIds
        : [];
      const employees = Array.isArray(this.payrollRun.employeeNumbers)
        ? this.payrollRun.employeeNumbers
        : [];
      const fromPayrollArray = (arr) => {
        return Object.fromEntries(
          arr.map((item) => {
            const key = item
              .replace(/^payroll\./, '')
              .replace(/_([a-z])/g, (_, c) => c.toUpperCase());
            return [key, true];
          }),
        );
      };
      const configs = fromPayrollArray(this.configList);
      this.isLoading = true;
      this.http
        .create({
          name: this.payrollRun.name,
          payPeriodId: this.payrollRun.payPeriodId?.id,
          periodStartDate: this.payrollRun.periodStartDate,
          periodEndDate: this.payrollRun.periodEndDate,
          payDate: this.payrollRun.payDate,
          status: 'draft',
          payslipTemplateId: this.payrollRun.payslipTemplateId?.id,
          configuration: {
            ...configs,
            employeeSelectionType: this.payrollRun.employeeSelectionType?.param,
            employeeNumbers: employees.map((item) => item.id),
            locationIds: locations.map((item) => item.id),
            subunitIds: subunits.map((item) => item.id),
          },
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.onCancel();
        })
        .catch((response) => {
          return this.$toast.warn({
            title: this.$t('general.warning'),
            message: response?.data.error.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped>
.no-config {
  font-style: italic;
  font-size: 10px !important;
}

.chip {
  display: flex;
  font-family: Nunito Sans, sans-serif;
  color: #64728c;
  background-color: #f6f7f9;
  margin-right: 5px;
  margin-bottom: 5px;
}

.orangehrm-button-row {
  gap: 0.4rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  max-width: 120px;
  margin-left: 60px;

  ::v-deep(.oxd-button--medium) {
    width: 100%;
  }

  @include oxd-respond-to('md') {
    margin-left: unset;
    max-width: unset;
    ::v-deep(.oxd-button--medium) {
      width: unset;
    }
  }
}
</style>
